import { useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import Layout from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { toast } from "sonner";
import { 
  Upload, 
  Folder,
  FileText, 
  Download, 
  Loader2,
  CheckCircle2,
  AlertCircle,
  TrendingUp,
  BarChart3,
  Table,
  X,
  FileCheck,
  Clock,
  Zap
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || import.meta.env.VITE_BACKEND_URL || window.location.origin;
const API = `${BACKEND_URL}/api`;

export default function BulkProcessing({ user, cabinet, onLogout }) {
  const navigate = useNavigate();
  const [files, setFiles] = useState([]);
  const [processing, setProcessing] = useState(false);
  const [progress, setProgress] = useState(0);
  const [results, setResults] = useState(null);
  const [uploadStats, setUploadStats] = useState({ total: 0, processed: 0, failed: 0 });
  const [analysisType, setAnalysisType] = useState("standard");
  const [customPrompt, setCustomPrompt] = useState("");

  const handleFilesUpload = (event) => {
    const selectedFiles = Array.from(event.target.files);
    if (selectedFiles.length === 0) return;

    if (selectedFiles.length > 40) {
      toast.error("Maximum 40 documents autorisés par traitement");
      return;
    }

    setFiles(selectedFiles);
    toast.success(`${selectedFiles.length} documents chargés`);
  };

  const handleDrop = (event) => {
    event.preventDefault();
    const droppedFiles = Array.from(event.dataTransfer.files);
    
    // Filter valid file types
    const validFiles = droppedFiles.filter(file => 
      file.type === 'application/pdf' || 
      file.name.endsWith('.docx') || 
      file.name.endsWith('.txt')
    );

    if (validFiles.length === 0) {
      toast.error("Aucun document valide (PDF, DOCX, TXT)");
      return;
    }

    if (validFiles.length > 40) {
      toast.error("Maximum 40 documents autorisés");
      return;
    }

    setFiles(validFiles);
    toast.success(`${validFiles.length} documents chargés`);
  };

  const handleDragOver = (event) => {
    event.preventDefault();
  };

  const processBulkDocuments = async () => {
    if (files.length === 0) {
      toast.error("Veuillez charger des documents");
      return;
    }

    if (analysisType === "custom" && !customPrompt.trim()) {
      toast.error("Veuillez entrer une instruction pour l'analyse personnalisée");
      return;
    }

    setProcessing(true);
    setProgress(0);
    setUploadStats({ total: files.length, processed: 0, failed: 0 });

    try {
      const token = localStorage.getItem('lawliet_token');
      const formData = new FormData();
      
      files.forEach(file => {
        formData.append('documents', file);
      });

      // Call bulk processing API
      const response = await axios.post(
        `${API}/documents/bulk-process`,
        formData,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'multipart/form-data'
          },
          params: { 
            cabinet_id: cabinet._id,
            analysis_type: analysisType,
            custom_prompt: analysisType === "custom" ? customPrompt : undefined
          },
          onUploadProgress: (progressEvent) => {
            const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
            setProgress(percentCompleted);
          }
        }
      );

      setResults(response.data);
      setUploadStats({
        total: response.data.total,
        processed: response.data.processed,
        failed: response.data.failed
      });
      
      toast.success(`${response.data.processed} documents traités avec succès !`);
    } catch (error) {
      console.error("Bulk processing error:", error);
      toast.error("Erreur lors du traitement en masse");
    } finally {
      setProcessing(false);
    }
  };

  const exportToExcel = () => {
    if (!results) return;

    // Generate CSV
    let csv = "Nom du Document,Type,Taille (KB),Pages,Mots,Parties Identifiées,Dates Clés,Montants,Statut,Risques\n";
    
    results.documents.forEach(doc => {
      csv += `"${doc.filename}",`;
      csv += `"${doc.type}",`;
      csv += `"${doc.size}",`;
      csv += `"${doc.pages || 'N/A'}",`;
      csv += `"${doc.word_count}",`;
      csv += `"${doc.parties?.join('; ') || 'N/A'}",`;
      csv += `"${doc.key_dates?.join('; ') || 'N/A'}",`;
      csv += `"${doc.amounts?.join('; ') || 'N/A'}",`;
      csv += `"${doc.status}",`;
      csv += `"${doc.risks?.join('; ') || 'Aucun'}"\n`;
    });

    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `analyse_bulk_${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success("Export Excel téléchargé !");
  };

  const resetBulkProcessing = () => {
    setFiles([]);
    setResults(null);
    setProgress(0);
    setUploadStats({ total: 0, processed: 0, failed: 0 });
  };

  const removeFile = (index) => {
    setFiles(files.filter((_, i) => i !== index));
  };

  return (
    <Layout user={user} cabinet={cabinet} onLogout={onLogout}>
      <div className="space-y-6">
        {/* Header */}
        <div>
          <h1 className="font-serif text-3xl font-semibold tracking-tight text-blue-900 mb-2 flex items-center gap-3">
            <Folder className="h-8 w-8" />
            Traitement en Masse
          </h1>
          <p className="text-slate-600">
            Analysez jusqu'à 40 documents simultanément avec extraction automatique de métadonnées et identification des risques
          </p>
        </div>

        {!results ? (
          <>
            {/* Upload Zone */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Upload className="h-5 w-5 text-blue-600" />
                  Chargement des Documents
                </CardTitle>
                <CardDescription>
                  Glissez-déposez un dossier complet ou sélectionnez plusieurs fichiers (PDF, DOCX, TXT)
                </CardDescription>
              </CardHeader>
              <CardContent>
                {files.length === 0 ? (
                  <label 
                    onDrop={handleDrop}
                    onDragOver={handleDragOver}
                    className="flex flex-col items-center justify-center p-16 border-2 border-dashed border-slate-300 rounded-lg cursor-pointer hover:border-blue-500 transition bg-slate-50 hover:bg-blue-50"
                  >
                    <Folder className="h-20 w-20 text-slate-400 mb-4" />
                    <span className="text-lg font-medium text-slate-700 mb-2">
                      Glissez un dossier complet ici
                    </span>
                    <span className="text-sm text-slate-500 mb-4">ou cliquez pour sélectionner des fichiers</span>
                    <Badge variant="outline" className="text-xs">
                      PDF, DOCX, TXT • Max 40 fichiers • 10MB par fichier
                    </Badge>
                    <input
                      type="file"
                      multiple
                      className="hidden"
                      accept=".pdf,.docx,.txt"
                      onChange={handleFilesUpload}
                    />
                  </label>
                ) : (
                  <div className="space-y-4">
                    <div className="flex items-center justify-between p-4 bg-green-50 border border-green-200 rounded-lg">
                      <div className="flex items-center gap-3">
                        <CheckCircle2 className="h-6 w-6 text-green-600" />
                        <div>
                          <p className="font-semibold text-green-900">
                            {files.length} documents chargés
                          </p>
                          <p className="text-sm text-green-700">
                            Taille totale: {(files.reduce((acc, f) => acc + f.size, 0) / 1024 / 1024).toFixed(2)} MB
                          </p>
                        </div>
                      </div>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={resetBulkProcessing}
                      >
                        Changer
                      </Button>
                    </div>

                    {/* File List Preview */}
                    <div className="border border-slate-200 rounded-lg max-h-[400px] overflow-y-auto">
                      <table className="w-full">
                        <thead className="bg-slate-50 sticky top-0">
                          <tr>
                            <th className="text-left text-xs font-semibold text-slate-600 p-3">Nom</th>
                            <th className="text-left text-xs font-semibold text-slate-600 p-3">Type</th>
                            <th className="text-left text-xs font-semibold text-slate-600 p-3">Taille</th>
                            <th className="text-right text-xs font-semibold text-slate-600 p-3">Action</th>
                          </tr>
                        </thead>
                        <tbody>
                          {files.slice(0, 50).map((file, idx) => (
                            <tr key={idx} className="border-t border-slate-100 hover:bg-slate-50">
                              <td className="text-sm text-slate-900 p-3 truncate max-w-xs">
                                {file.name}
                              </td>
                              <td className="text-sm text-slate-600 p-3">
                                <Badge variant="outline" className="text-xs">
                                  {file.name.split('.').pop().toUpperCase()}
                                </Badge>
                              </td>
                              <td className="text-sm text-slate-600 p-3">
                                {(file.size / 1024).toFixed(1)} KB
                              </td>
                              <td className="text-right p-3">
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => removeFile(idx)}
                                  className="h-7 w-7 p-0"
                                >
                                  <X className="h-4 w-4" />
                                </Button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                      {files.length > 50 && (
                        <div className="p-3 text-center text-sm text-slate-500 bg-slate-50 border-t">
                          ... et {files.length - 50} autres documents
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Analysis Type Selection */}
            {files.length > 0 && (
              <Card className="bg-blue-50 border-blue-200">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-blue-900">
                    <BarChart3 className="h-5 w-5" />
                    Type d'Analyse
                  </CardTitle>
                  <CardDescription>
                    Choisissez le type d'analyse IA à appliquer sur vos documents
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <button
                      onClick={() => setAnalysisType("standard")}
                      className={`p-4 border-2 rounded-lg text-left transition ${
                        analysisType === "standard"
                          ? "border-blue-600 bg-blue-100"
                          : "border-slate-300 bg-white hover:border-blue-400"
                      }`}
                    >
                      <div className="flex items-start gap-3">
                        <FileCheck className={`h-5 w-5 mt-1 ${analysisType === "standard" ? "text-blue-600" : "text-slate-500"}`} />
                        <div>
                          <h4 className={`font-semibold ${analysisType === "standard" ? "text-blue-900" : "text-slate-900"}`}>
                            Analyse Standard
                          </h4>
                          <p className="text-sm text-slate-600 mt-1">
                            Extraction parties, dates, montants, risques
                          </p>
                        </div>
                      </div>
                    </button>

                    <button
                      onClick={() => setAnalysisType("comparative")}
                      className={`p-4 border-2 rounded-lg text-left transition ${
                        analysisType === "comparative"
                          ? "border-blue-600 bg-blue-100"
                          : "border-slate-300 bg-white hover:border-blue-400"
                      }`}
                    >
                      <div className="flex items-start gap-3">
                        <Table className={`h-5 w-5 mt-1 ${analysisType === "comparative" ? "text-blue-600" : "text-slate-500"}`} />
                        <div>
                          <h4 className={`font-semibold ${analysisType === "comparative" ? "text-blue-900" : "text-slate-900"}`}>
                            Analyse Comparative
                          </h4>
                          <p className="text-sm text-slate-600 mt-1">
                            Compare documents, détecte patterns et outliers
                          </p>
                        </div>
                      </div>
                    </button>

                    <button
                      onClick={() => setAnalysisType("due_diligence")}
                      className={`p-4 border-2 rounded-lg text-left transition ${
                        analysisType === "due_diligence"
                          ? "border-blue-600 bg-blue-100"
                          : "border-slate-300 bg-white hover:border-blue-400"
                      }`}
                    >
                      <div className="flex items-start gap-3">
                        <CheckCircle2 className={`h-5 w-5 mt-1 ${analysisType === "due_diligence" ? "text-blue-600" : "text-slate-500"}`} />
                        <div>
                          <h4 className={`font-semibold ${analysisType === "due_diligence" ? "text-blue-900" : "text-slate-900"}`}>
                            Due Diligence
                          </h4>
                          <p className="text-sm text-slate-600 mt-1">
                            Checklist juridique complète (8 critères)
                          </p>
                        </div>
                      </div>
                    </button>

                    <button
                      onClick={() => setAnalysisType("custom")}
                      className={`p-4 border-2 rounded-lg text-left transition ${
                        analysisType === "custom"
                          ? "border-blue-600 bg-blue-100"
                          : "border-slate-300 bg-white hover:border-blue-400"
                      }`}
                    >
                      <div className="flex items-start gap-3">
                        <Zap className={`h-5 w-5 mt-1 ${analysisType === "custom" ? "text-blue-600" : "text-slate-500"}`} />
                        <div>
                          <h4 className={`font-semibold ${analysisType === "custom" ? "text-blue-900" : "text-slate-900"}`}>
                            Analyse Personnalisée
                          </h4>
                          <p className="text-sm text-slate-600 mt-1">
                            Définissez votre propre instruction d'analyse
                          </p>
                        </div>
                      </div>
                    </button>
                  </div>

                  {analysisType === "custom" && (
                    <div className="mt-4 p-4 bg-white border-2 border-blue-300 rounded-lg">
                      <label className="block text-sm font-semibold text-slate-900 mb-2">
                        Instruction d'analyse personnalisée
                      </label>
                      <textarea
                        value={customPrompt}
                        onChange={(e) => setCustomPrompt(e.target.value)}
                        placeholder="Ex: Identifie toutes les clauses de non-concurrence et vérifie leur conformité avec le droit français du travail..."
                        className="w-full p-3 border border-slate-300 rounded-lg text-sm resize-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        rows={4}
                      />
                      <p className="text-xs text-slate-500 mt-2">
                        Décrivez précisément ce que l'IA doit rechercher ou analyser dans vos documents
                      </p>
                    </div>
                  )}
                </CardContent>
              </Card>
            )}

            {/* Processing Button */}
            {files.length > 0 && (
              <div className="flex justify-center">
                <Button
                  size="lg"
                  onClick={processBulkDocuments}
                  disabled={processing}
                  className="bg-blue-600 hover:bg-blue-700 min-w-[300px]"
                >
                  {processing ? (
                    <>
                      <Loader2 className="h-5 w-5 mr-2 animate-spin" />
                      Traitement en cours...
                    </>
                  ) : (
                    <>
                      <Zap className="h-5 w-5 mr-2" />
                      Analyser {files.length} Documents
                    </>
                  )}
                </Button>
              </div>
            )}

            {/* Processing Progress */}
            {processing && (
              <Card>
                <CardContent className="py-8">
                  <div className="space-y-4">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm font-medium text-slate-700">
                        Traitement en cours...
                      </span>
                      <span className="text-sm font-semibold text-blue-900">
                        {uploadStats.processed}/{uploadStats.total}
                      </span>
                    </div>
                    <Progress value={progress} className="h-3" />
                    <div className="grid grid-cols-3 gap-4 pt-4">
                      <div className="text-center">
                        <p className="text-2xl font-bold text-slate-900">{uploadStats.total}</p>
                        <p className="text-xs text-slate-500">Total</p>
                      </div>
                      <div className="text-center">
                        <p className="text-2xl font-bold text-green-600">{uploadStats.processed}</p>
                        <p className="text-xs text-slate-500">Traités</p>
                      </div>
                      <div className="text-center">
                        <p className="text-2xl font-bold text-red-600">{uploadStats.failed}</p>
                        <p className="text-xs text-slate-500">Échecs</p>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Info Card */}
            <Card className="bg-gradient-to-r from-blue-50 to-indigo-50 border-blue-200">
              <CardContent className="py-6">
                <div className="flex items-start gap-4">
                  <div className="p-3 bg-blue-100 rounded-lg">
                    <BarChart3 className="h-6 w-6 text-blue-600" />
                  </div>
                  <div className="flex-1">
                    <h3 className="font-bold text-blue-900 mb-2">
                      Analyse IA Automatisée
                    </h3>
                    <ul className="text-sm text-slate-700 space-y-2">
                      <li className="flex items-center gap-2">
                        <CheckCircle2 className="h-4 w-4 text-green-600" />
                        <span>Extraction parties contractantes, dates, montants</span>
                      </li>
                      <li className="flex items-center gap-2">
                        <CheckCircle2 className="h-4 w-4 text-green-600" />
                        <span>Identification automatique des clauses à risque</span>
                      </li>
                      <li className="flex items-center gap-2">
                        <CheckCircle2 className="h-4 w-4 text-green-600" />
                        <span>Classification par type de document</span>
                      </li>
                      <li className="flex items-center gap-2">
                        <CheckCircle2 className="h-4 w-4 text-green-600" />
                        <span>Export Excel avec toutes les métadonnées</span>
                      </li>
                    </ul>
                  </div>
                </div>
              </CardContent>
            </Card>
          </>
        ) : (
          <>
            {/* Results Dashboard */}
            <div className="space-y-6">
              {/* Summary Stats */}
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <Card>
                  <CardContent className="pt-6">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm font-medium text-slate-600">Documents Traités</p>
                        <p className="text-3xl font-bold text-blue-900">{results.processed}</p>
                      </div>
                      <FileCheck className="h-10 w-10 text-blue-600" />
                    </div>
                  </CardContent>
                </Card>

                <Card>
                  <CardContent className="pt-6">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm font-medium text-slate-600">Temps Total</p>
                        <p className="text-3xl font-bold text-green-600">{results.processing_time}s</p>
                      </div>
                      <Clock className="h-10 w-10 text-green-600" />
                    </div>
                  </CardContent>
                </Card>

                <Card>
                  <CardContent className="pt-6">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm font-medium text-slate-600">Risques Identifiés</p>
                        <p className="text-3xl font-bold text-orange-600">{results.total_risks || 0}</p>
                      </div>
                      <AlertCircle className="h-10 w-10 text-orange-600" />
                    </div>
                  </CardContent>
                </Card>

                <Card>
                  <CardContent className="pt-6">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm font-medium text-slate-600">Mots Analysés</p>
                        <p className="text-3xl font-bold text-purple-600">{results.total_words?.toLocaleString()}</p>
                      </div>
                      <TrendingUp className="h-10 w-10 text-purple-600" />
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* Actions */}
              <Card>
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <CardTitle>Résultats de l'Analyse</CardTitle>
                    <div className="flex gap-2">
                      <Button onClick={exportToExcel}>
                        <Download className="h-4 w-4 mr-2" />
                        Export Excel
                      </Button>
                      <Button variant="outline" onClick={resetBulkProcessing}>
                        Nouveau Traitement
                      </Button>
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="border border-slate-200 rounded-lg overflow-hidden">
                    <div className="overflow-x-auto max-h-[600px]">
                      <table className="w-full">
                        <thead className="bg-slate-50 sticky top-0">
                          <tr>
                            <th className="text-left text-xs font-semibold text-slate-600 p-3">Document</th>
                            <th className="text-left text-xs font-semibold text-slate-600 p-3">Type</th>
                            
                            {/* Standard Analysis Columns */}
                            {results.analysis_type === "standard" && (
                              <>
                                <th className="text-left text-xs font-semibold text-slate-600 p-3">Parties</th>
                                <th className="text-left text-xs font-semibold text-slate-600 p-3">Dates</th>
                                <th className="text-left text-xs font-semibold text-slate-600 p-3">Montants</th>
                                <th className="text-left text-xs font-semibold text-slate-600 p-3">Risques</th>
                              </>
                            )}

                            {/* Due Diligence Columns */}
                            {results.analysis_type === "due_diligence" && (
                              <>
                                <th className="text-left text-xs font-semibold text-slate-600 p-3">Score DD</th>
                                <th className="text-left text-xs font-semibold text-slate-600 p-3">Issues Critiques</th>
                                <th className="text-left text-xs font-semibold text-slate-600 p-3">Recommandation</th>
                              </>
                            )}

                            {/* Comparative Columns */}
                            {results.analysis_type === "comparative" && (
                              <>
                                <th className="text-left text-xs font-semibold text-slate-600 p-3">Parties</th>
                                <th className="text-left text-xs font-semibold text-slate-600 p-3">Clauses Clés</th>
                                <th className="text-left text-xs font-semibold text-slate-600 p-3">Mots</th>
                              </>
                            )}

                            {/* Custom Columns */}
                            {results.analysis_type === "custom" && (
                              <>
                                <th className="text-left text-xs font-semibold text-slate-600 p-3">Résumé</th>
                                <th className="text-left text-xs font-semibold text-slate-600 p-3">Findings</th>
                                <th className="text-left text-xs font-semibold text-slate-600 p-3">Recommandation</th>
                              </>
                            )}

                            <th className="text-left text-xs font-semibold text-slate-600 p-3">Statut</th>
                          </tr>
                        </thead>
                        <tbody>
                          {results.documents?.map((doc, idx) => (
                            <tr key={idx} className="border-t border-slate-100 hover:bg-slate-50">
                              <td className="text-sm text-slate-900 p-3">
                                <div className="flex items-center gap-2">
                                  <FileText className="h-4 w-4 text-blue-600" />
                                  <span className="truncate max-w-[200px]">{doc.filename}</span>
                                </div>
                              </td>
                              <td className="text-sm p-3">
                                <Badge variant="outline" className="text-xs">
                                  {doc.type}
                                </Badge>
                              </td>

                              {/* Standard Analysis Data */}
                              {results.analysis_type === "standard" && (
                                <>
                                  <td className="text-sm text-slate-600 p-3 max-w-[150px] truncate">
                                    {doc.parties?.join(', ') || 'N/A'}
                                  </td>
                                  <td className="text-sm text-slate-600 p-3">
                                    {doc.key_dates?.length || 0} dates
                                  </td>
                                  <td className="text-sm text-slate-600 p-3">
                                    {doc.amounts?.length || 0} montants
                                  </td>
                                  <td className="text-sm p-3">
                                    {doc.risks && doc.risks.length > 0 ? (
                                      <Badge variant="destructive" className="text-xs">
                                        {doc.risks.length} risques
                                      </Badge>
                                    ) : (
                                      <Badge variant="outline" className="text-xs text-green-600">
                                        Aucun
                                      </Badge>
                                    )}
                                  </td>
                                </>
                              )}

                              {/* Due Diligence Data */}
                              {results.analysis_type === "due_diligence" && (
                                <>
                                  <td className="text-sm p-3">
                                    <Badge variant="outline" className="text-xs font-semibold">
                                      {doc.checklist_score || 'N/A'}
                                    </Badge>
                                  </td>
                                  <td className="text-sm text-slate-600 p-3 max-w-[200px]">
                                    {doc.critical_issues?.length > 0 ? (
                                      <span className="text-red-600 font-medium">{doc.critical_issues.length} issues</span>
                                    ) : (
                                      <span className="text-green-600">Aucune</span>
                                    )}
                                  </td>
                                  <td className="text-sm text-slate-600 p-3 max-w-[200px] truncate">
                                    {doc.recommendation || 'N/A'}
                                  </td>
                                </>
                              )}

                              {/* Comparative Data */}
                              {results.analysis_type === "comparative" && (
                                <>
                                  <td className="text-sm text-slate-600 p-3 max-w-[150px] truncate">
                                    {doc.parties?.join(', ') || 'N/A'}
                                  </td>
                                  <td className="text-sm text-slate-600 p-3">
                                    {doc.key_clauses?.length || 0} clauses
                                  </td>
                                  <td className="text-sm text-slate-600 p-3">
                                    {doc.word_count?.toLocaleString() || 0}
                                  </td>
                                </>
                              )}

                              {/* Custom Data */}
                              {results.analysis_type === "custom" && (
                                <>
                                  <td className="text-sm text-slate-600 p-3 max-w-[200px] truncate">
                                    {doc.summary || 'N/A'}
                                  </td>
                                  <td className="text-sm text-slate-600 p-3">
                                    {doc.key_findings?.length || 0} findings
                                  </td>
                                  <td className="text-sm text-slate-600 p-3 max-w-[150px] truncate">
                                    {doc.recommendation || 'N/A'}
                                  </td>
                                </>
                              )}

                              <td className="text-sm p-3">
                                <Badge 
                                  className={`text-xs ${
                                    doc.status === 'success' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                                  }`}
                                >
                                  {doc.status === 'success' ? '✓ Traité' : '✗ Échec'}
                                </Badge>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Comparative Analysis Results */}
              {results.comparative_analysis && (
                <Card className="bg-gradient-to-r from-purple-50 to-pink-50 border-purple-200">
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2 text-purple-900">
                      <TrendingUp className="h-5 w-5" />
                      Analyse Comparative IA
                    </CardTitle>
                    <CardDescription>Insights transversaux sur l'ensemble des documents</CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    {results.comparative_analysis.patterns && (
                      <div>
                        <h4 className="font-semibold text-slate-900 mb-2 flex items-center gap-2">
                          <CheckCircle2 className="h-4 w-4 text-green-600" />
                          Patterns Communs
                        </h4>
                        <ul className="space-y-1 ml-6">
                          {results.comparative_analysis.patterns.map((pattern, idx) => (
                            <li key={idx} className="text-sm text-slate-700">• {pattern}</li>
                          ))}
                        </ul>
                      </div>
                    )}
                    
                    {results.comparative_analysis.outliers && (
                      <div>
                        <h4 className="font-semibold text-slate-900 mb-2 flex items-center gap-2">
                          <AlertCircle className="h-4 w-4 text-orange-600" />
                          Outliers / Anomalies
                        </h4>
                        <ul className="space-y-1 ml-6">
                          {results.comparative_analysis.outliers.map((outlier, idx) => (
                            <li key={idx} className="text-sm text-slate-700">• {outlier}</li>
                          ))}
                        </ul>
                      </div>
                    )}
                    
                    {results.comparative_analysis.recommendations && (
                      <div>
                        <h4 className="font-semibold text-slate-900 mb-2 flex items-center gap-2">
                          <Zap className="h-4 w-4 text-blue-600" />
                          Recommandations
                        </h4>
                        <ul className="space-y-1 ml-6">
                          {results.comparative_analysis.recommendations.map((rec, idx) => (
                            <li key={idx} className="text-sm text-slate-700">• {rec}</li>
                          ))}
                        </ul>
                      </div>
                    )}

                    {results.comparative_analysis.summary && (
                      <div className="p-3 bg-white rounded-lg border border-purple-200">
                        <p className="text-sm text-slate-700">{results.comparative_analysis.summary}</p>
                      </div>
                    )}
                  </CardContent>
                </Card>
              )}

              {/* Summary by Type */}
              {results.summary_by_type && (
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <BarChart3 className="h-5 w-5 text-blue-600" />
                      Répartition par Type de Document
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                      {Object.entries(results.summary_by_type).map(([type, count]) => (
                        <div key={type} className="bg-slate-50 border border-slate-200 rounded-lg p-4 text-center">
                          <p className="text-2xl font-bold text-slate-900">{count}</p>
                          <p className="text-sm text-slate-600 mt-1">{type}</p>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}
            </div>
          </>
        )}
      </div>
    </Layout>
  );
}
